import React, { useState, useEffect, useCallback } from "react";
import useAxiosPrivate from "../../context/useAxiosPrivate";
import "./DeleteVersions.css";

export const DeleteVersions = () => {
  const [factories, setFactories] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);
  const [factoryData, setFactoryData] = useState(null);
  const [loadingFactories, setLoadingFactories] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [availableVersionsByUser, setAvailableVersionsByUser] = useState({});
  const [deletingModelId, setDeletingModelId] = useState(null);
  const [error, setError] = useState(null);
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    const getFactories = async () => {
      setLoadingFactories(true);
      setError(null);
      try {
        const res = await axiosPrivate.get("/Users/Factory");
        setFactories(res.data || []);
      } catch (e) {
        console.log("getFactories error ", e);
        setError("Не удалось загрузить список заводов");
      } finally {
        setLoadingFactories(false);
      }
    };
    getFactories();
  }, [axiosPrivate]);

  const loadVersionsForUsers = useCallback(
    async (userIds) => {
      if (!userIds.length) return;
      setLoadingVersions(true);
      try {
        const results = await Promise.allSettled(
          userIds.map((userId) =>
            axiosPrivate.get(`/UserAdministration/AvailableVersions/${userId}`)
          )
        );
        const next = {};
        results.forEach((res, i) => {
          const userId = userIds[i];
          if (res.status === "fulfilled" && res.value?.data) {
            next[userId] = res.value.data;
          } else {
            next[userId] = [];
          }
        });
        setAvailableVersionsByUser((prev) => ({ ...prev, ...next }));
      } catch (e) {
        console.log("loadVersionsForUsers error ", e);
        setError("Не удалось загрузить доступные версии");
      } finally {
        setLoadingVersions(false);
      }
    },
    [axiosPrivate]
  );

  useEffect(() => {
    if (!factoryData || factoryData.length === 0) {
      setAvailableVersionsByUser({});
      return;
    }
    const userIds = [];
    factoryData.forEach((item) => {
      if (item.users && Array.isArray(item.users)) {
        item.users.forEach((u) => userIds.push(u.id));
      }
    });
    loadVersionsForUsers(userIds);
  }, [factoryData, loadVersionsForUsers]);

  const handleFactoryClick = async (factoryId) => {
    setSelectedFactoryId(factoryId);
    setLoadingDetails(true);
    setFactoryData(null);
    setAvailableVersionsByUser({});
    setError(null);
    try {
      const res = await axiosPrivate.get(`/Users/GetByFactory/${factoryId}`);
      setFactoryData(res.data || []);
    } catch (e) {
      console.log("getUsersByFactoryId error ", e);
      setError("Не удалось загрузить компании и пользователей");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDeleteVersion = async (userId, modelId) => {
    setDeletingModelId(modelId);
    setError(null);
    try {
      await axiosPrivate.delete(`/UserAdministration/DeleteUserModel/${modelId}`);
      const res = await axiosPrivate.get(`/UserAdministration/AvailableVersions/${userId}`);
      setAvailableVersionsByUser((prev) => ({
        ...prev,
        [userId]: res.data || [],
      }));
    } catch (e) {
      console.log("DeleteUserModel error ", e);
      setError(e.response?.data?.message || "Не удалось удалить версию");
    } finally {
      setDeletingModelId(null);
    }
  };

  return (
    <div className="delete-versions">
      <h2 className="delete-versions__title">Удаление версий</h2>

      {error && <p className="delete-versions__error">{error}</p>}

      <section className="delete-versions__factories">
        <h3 className="delete-versions__section-title">Заводы</h3>
        {loadingFactories ? (
          <p className="delete-versions__loading">Загрузка списка заводов...</p>
        ) : factories.length === 0 ? (
          <p className="delete-versions__empty">Нет доступных заводов</p>
        ) : (
          <ul className="delete-versions__factory-list">
            {factories.map((factory) => (
              <li key={factory.id} className="delete-versions__factory-item">
                <button
                  type="button"
                  className={`delete-versions__factory-btn ${selectedFactoryId === factory.id ? "delete-versions__factory-btn_active" : ""}`}
                  onClick={() => handleFactoryClick(factory.id)}
                >
                  {factory.factory}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedFactoryId && (
        <section className="delete-versions__details">
          <h3 className="delete-versions__section-title">Компании и пользователи</h3>
          {loadingDetails ? (
            <p className="delete-versions__loading">Загрузка...</p>
          ) : factoryData && factoryData.length > 0 ? (
            <>
              {loadingVersions && (
                <p className="delete-versions__loading">Загрузка версий...</p>
              )}
              <ul className="delete-versions__company-list">
                {factoryData.map((item, companyIndex) => (
                  <li key={companyIndex} className="delete-versions__company-item">
                    <div className="delete-versions__company-name">{item.companyName}</div>
                    {item.users && item.users.length > 0 ? (
                      <ul className="delete-versions__user-list">
                        {item.users.map((user) => {
                          const versions = availableVersionsByUser[user.id] || [];
                          return (
                            <li key={user.id} className="delete-versions__user-item">
                              <div className="delete-versions__user-info">
                                <span className="delete-versions__user-name">
                                  {user.name}
                                  {user.description ? ` — ${user.description}` : ""}
                                </span>
                              </div>
                              <div className="delete-versions__versions">
                                {versions.length === 0 && !loadingVersions ? (
                                  <span className="delete-versions__no-versions">Нет доступных версий</span>
                                ) : (
                                  versions.map((v) => (
                                    <button
                                      key={v.modelId}
                                      type="button"
                                      className="delete-versions__version-chip"
                                      disabled={deletingModelId === v.modelId}
                                      onClick={() => handleDeleteVersion(user.id, v.modelId)}
                                      title="Удалить версию"
                                    >
                                      <span className="delete-versions__version-label">
                                        {v.version != null ? v.version : `ID: ${v.modelId}`}
                                        {v.isActive && (
                                          <span className="delete-versions__version-active"> активна</span>
                                        )}
                                      </span>
                                      {deletingModelId === v.modelId ? (
                                        <span className="delete-versions__version-deleting">…</span>
                                      ) : (
                                        <span className="delete-versions__version-delete">✕</span>
                                      )}
                                    </button>
                                  ))
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="delete-versions__no-users">Нет пользователей</p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="delete-versions__empty">Нет компаний и пользователей</p>
          )}
        </section>
      )}
    </div>
  );
};
